/**
 * Views read the pool through these. Each returns `null` while the pool
 * has no copy — the view then fetches for itself, exactly as it did before
 * the sync engine — and a value otherwise, recomputed only when a table it
 * reads changes.
 */
import { useMemo, useSyncExternalStore } from 'react';
import {
  AttachmentReturn,
  CommentReturn,
  CompanyReturn,
  HoaReturn,
  HomeReturn,
  MemberReturn,
  NotificationResponse,
  UserReturn,
  VendorReturn,
  ViolationReturn,
  WorkOrderReturn,
} from '../clients/rust-api-client';
import { Fines, useFines } from './fines';
import { ModelName, SyncedAttachment, SyncRows } from './models';
import { syncStore } from './store';

const useReady = (): boolean => useSyncExternalStore(syncStore.subscribe, syncStore.isReady);

/** One model's rows as views should show them; for hooks over the copy. */
export const useTable = <K extends ModelName>(model: K): ReadonlyMap<string, SyncRows[K]> =>
  useSyncExternalStore(syncStore.subscribe, () => syncStore.table(model));

export const values = <T>(table: ReadonlyMap<string, T>): T[] => Array.from(table.values());

/** Whether the pool holds a copy at all. */
export const useSyncReady = useReady;

// --- associations --------------------------------------------------------

export function useSyncedHoas(): HoaReturn[] | null {
  const ready = useReady();
  const hoas = useTable('hoa');
  return useMemo(
    () => (ready ? values(hoas).sort((a, b) => a.name.localeCompare(b.name)) : null),
    [ready, hoas]
  );
}

/** `undefined`: the pool is ready and does not have it — fetch it. */
export function useSyncedHoa(id: string | undefined): HoaReturn | null | undefined {
  const ready = useReady();
  const hoas = useTable('hoa');
  return useMemo(() => (ready ? (id ? hoas.get(id) : undefined) : null), [ready, hoas, id]);
}

// --- homes ------------------------------------------------------------------

export function useSyncedHomes(filter: {
  hoaId?: string | null;
  memberId?: string | null;
}): HomeReturn[] | null {
  const ready = useReady();
  const homes = useTable('home');
  const links = useTable('memberHome');
  const { hoaId, memberId } = filter;
  return useMemo(() => {
    if (!ready) return null;
    let rows = values(homes);
    if (hoaId) rows = rows.filter((h) => h.hoa_id === hoaId);
    if (memberId) {
      const mine = new Set(
        values(links)
          .filter((l) => l.member_id === memberId)
          .map((l) => l.home_id)
      );
      rows = rows.filter((h) => mine.has(h.id));
    }
    return rows;
  }, [ready, homes, links, hoaId, memberId]);
}

export function useSyncedHome(id: string | undefined): HomeReturn | null | undefined {
  const ready = useReady();
  const homes = useTable('home');
  return useMemo(() => (ready ? (id ? homes.get(id) : undefined) : null), [ready, homes, id]);
}

// --- members ----------------------------------------------------------------

export function useSyncedMembers(filter: {
  hoaId?: string | null;
  homeId?: string | null;
}): MemberReturn[] | null {
  const ready = useReady();
  const members = useTable('member');
  const links = useTable('memberHome');
  const homes = useTable('home');
  const { hoaId, homeId } = filter;
  return useMemo(() => {
    if (!ready) return null;
    let homeIds: Set<string> | null = null;
    if (homeId) homeIds = new Set([homeId]);
    else if (hoaId) {
      homeIds = new Set(
        values(homes)
          .filter((h) => h.hoa_id === hoaId)
          .map((h) => h.id)
      );
    }
    if (!homeIds) return values(members);
    const ids = new Set(
      values(links)
        .filter((l) => homeIds!.has(l.home_id))
        .map((l) => l.member_id)
    );
    return values(members).filter((m) => ids.has(m.id));
  }, [ready, members, links, homes, hoaId, homeId]);
}

export function useSyncedMember(id: string | undefined): MemberReturn | null | undefined {
  const ready = useReady();
  const members = useTable('member');
  return useMemo(() => (ready ? (id ? members.get(id) : undefined) : null), [ready, members, id]);
}

// --- attachments ------------------------------------------------------------

/** Each parent's attachments, in their order, shaped as REST nests them. */
const attachmentsOf = (
  table: ReadonlyMap<string, SyncedAttachment>,
  entityType: SyncedAttachment['entity_type']
): Map<string, AttachmentReturn[]> => {
  const out = new Map<string, AttachmentReturn[]>();
  table.forEach((a) => {
    if (a.entity_type !== entityType) return;
    const { entity_type: _t, entity_id: parent, ...attachment } = a;
    const list = out.get(parent) ?? [];
    list.push(attachment);
    out.set(parent, list);
  });
  out.forEach((list) => list.sort((a, b) => a.sort_order - b.sort_order));
  return out;
};

// --- violations ---------------------------------------------------------------

/**
 * A synced violation as REST returns it: its photos joined back on from
 * the `attachment` table, and its fine from the REST fines this session
 * has read (`fines.ts`) — the engine carries neither money nor nested
 * rows.
 */
const asReturned = (
  v: ViolationReturn,
  photos: Map<string, AttachmentReturn[]>,
  fines: Fines
): ViolationReturn => ({
  ...v,
  attachments: photos.get(v.id) ?? [],
  fine: fines.has(v.id) ? fines.get(v.id) : v.fine,
});

export function useSyncedViolations(filter: {
  hoaId?: string | null;
  homeId?: string | null;
  memberId?: string | null;
}): ViolationReturn[] | null {
  const ready = useReady();
  const violations = useTable('violation');
  const homes = useTable('home');
  const links = useTable('memberHome');
  const attachments = useTable('attachment');
  const fines = useFines();
  const { hoaId, homeId, memberId } = filter;
  return useMemo(() => {
    if (!ready) return null;
    const photos = attachmentsOf(attachments, 'Violation');
    let rows = values(violations).map((v) => asReturned(v, photos, fines));
    if (homeId) rows = rows.filter((v) => v.home_id === homeId);
    if (hoaId) {
      const inHoa = new Set(
        values(homes)
          .filter((h) => h.hoa_id === hoaId)
          .map((h) => h.id)
      );
      rows = rows.filter((v) => inHoa.has(v.home_id));
    }
    if (memberId) {
      const theirs = new Set(
        values(links)
          .filter((l) => l.member_id === memberId)
          .map((l) => l.home_id)
      );
      rows = rows.filter((v) => theirs.has(v.home_id));
    }
    return rows;
  }, [ready, violations, homes, links, attachments, fines, hoaId, homeId, memberId]);
}

export function useSyncedViolation(id: string | undefined): ViolationReturn | null | undefined {
  const ready = useReady();
  const violations = useTable('violation');
  const attachments = useTable('attachment');
  const fines = useFines();
  return useMemo(() => {
    if (!ready) return null;
    const v = id ? violations.get(id) : undefined;
    return v && asReturned(v, attachmentsOf(attachments, 'Violation'), fines);
  }, [ready, violations, attachments, fines, id]);
}

// --- comments ---------------------------------------------------------------------

/**
 * A violation's or work order's thread, as the REST thread returns it to
 * this viewer: oldest first, deleted comments left out, photos nested. A
 * board member's copy never held the internal thread. `null` without a
 * copy, and always for a reservation, whose thread does not sync.
 */
export function useSyncedComments(subject: {
  kind: 'violation' | 'work_order' | 'reservation';
  id: string;
}): CommentReturn[] | null {
  const ready = useReady();
  const comments = useTable('comment');
  const attachments = useTable('attachment');
  const { kind, id } = subject;
  return useMemo(() => {
    if (!ready || kind === 'reservation') return null;
    const photos = attachmentsOf(attachments, 'Comment');
    return values(comments)
      .filter((c) =>
        kind === 'violation' ? c.violation_id === id : c.work_order_id === id
      )
      .filter((c) => !c.is_deleted)
      .map((c) => ({ ...c, attachments: photos.get(c.id) ?? [] }))
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  }, [ready, comments, attachments, kind, id]);
}

/**
 * Every violation and work-order comment this viewer may read, deleted
 * ones left out, without photos — Home's activity (#670). A board
 * member's copy never held the internal thread.
 */
export function useSyncedAllComments(): CommentReturn[] | null {
  const ready = useReady();
  const comments = useTable('comment');
  return useMemo(
    () => (ready ? values(comments).filter((c) => !c.is_deleted) : null),
    [ready, comments]
  );
}

// --- notifications -----------------------------------------------------------------

/**
 * Notifications addressed to staff in this viewer's associations, newest
 * first — the Notifications view. The association's name is read from the
 * copy, so a renamed association is right here too.
 */
export function useSyncedStaffNotifications(): NotificationResponse[] | null {
  const ready = useReady();
  const notifications = useTable('notification');
  const hoas = useTable('hoa');
  return useMemo(() => {
    if (!ready) return null;
    // The table holds what went to homeowners too (Communications).
    return values(notifications)
      .filter((n) => n.is_for_staff)
      .map((n) => ({ ...n, hoa_name: hoas.get(n.hoa_id)?.name ?? n.hoa_name }))
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  }, [ready, notifications, hoas]);
}

/**
 * Communications: what was sent to homeowners, newest first, as the list
 * endpoint gives it to management.
 *
 * **Not for a board member** — pass `enabled: false`. The copy leaves the
 * board out of residents' mail until #626 decides whether it should read
 * it; the API still gives it to them, so their view keeps fetching.
 */
export function useSyncedCommunications(enabled: boolean): NotificationResponse[] | null {
  const ready = useReady();
  const notifications = useTable('notification');
  const hoas = useTable('hoa');
  return useMemo(() => {
    if (!ready || !enabled) return null;
    return values(notifications)
      .filter((n) => n.is_for_member)
      .map((n) => ({ ...n, hoa_name: hoas.get(n.hoa_id)?.name ?? n.hoa_name }))
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  }, [ready, enabled, notifications, hoas]);
}

export function useSyncedNotification(
  id: string | undefined
): NotificationResponse | null | undefined {
  const ready = useReady();
  const notifications = useTable('notification');
  return useMemo(
    () => (ready ? (id ? notifications.get(id) : undefined) : null),
    [ready, notifications, id]
  );
}

// --- work orders and vendors -----------------------------------------------------

export function useSyncedWorkOrders(filter: {
  hoaId?: string | null;
  homeId?: string | null;
}): WorkOrderReturn[] | null {
  const ready = useReady();
  const orders = useTable('workOrder');
  const { hoaId, homeId } = filter;
  return useMemo(() => {
    if (!ready) return null;
    return values(orders).filter(
      (w) => (!hoaId || w.hoa_id === hoaId) && (!homeId || w.home_id === homeId)
    );
  }, [ready, orders, hoaId, homeId]);
}

export function useSyncedWorkOrder(id: string | undefined): WorkOrderReturn | null | undefined {
  const ready = useReady();
  const orders = useTable('workOrder');
  return useMemo(() => (ready ? (id ? orders.get(id) : undefined) : null), [ready, orders, id]);
}

export function useSyncedVendors(filter: { hoaId?: string | null }): VendorReturn[] | null {
  const ready = useReady();
  const vendors = useTable('vendor');
  const { hoaId } = filter;
  return useMemo(() => {
    if (!ready) return null;
    return values(vendors).filter((v) => !hoaId || v.hoa_id === hoaId);
  }, [ready, vendors, hoaId]);
}

export function useSyncedVendor(id: string | undefined): VendorReturn | null | undefined {
  const ready = useReady();
  const vendors = useTable('vendor');
  return useMemo(() => (ready ? (id ? vendors.get(id) : undefined) : null), [ready, vendors, id]);
}

// --- companies and staff -----------------------------------------------------------

export function useSyncedCompany(id: string | null | undefined): CompanyReturn | null | undefined {
  const ready = useReady();
  const companies = useTable('company');
  return useMemo(
    () => (ready ? (id ? companies.get(id) : undefined) : null),
    [ready, companies, id]
  );
}

/**
 * A staff account. The pool holds every user of the company only for an
 * Owner or Admin — the people `GET /user` lists them for — and otherwise
 * just the viewer, so `undefined` here is common: fetch it.
 */
export function useSyncedUser(id: string | null | undefined): UserReturn | null | undefined {
  const ready = useReady();
  const users = useTable('user');
  return useMemo(() => (ready ? (id ? users.get(id) : undefined) : null), [ready, users, id]);
}

/** Every staff account in the pool, for a company's user list. */
export function useSyncedUsers(filter: { companyId?: string | null }): UserReturn[] | null {
  const ready = useReady();
  const users = useTable('user');
  const { companyId } = filter;
  return useMemo(() => {
    if (!ready) return null;
    return values(users).filter((u) => !companyId || u.company_id === companyId);
  }, [ready, users, companyId]);
}

// --- detail pages -------------------------------------------------------------------

type Stamped = {
  id: string;
  date_updated?: number | string | null;
  updated_at?: number | string | null;
};

const stamp = (row: Stamped): number => {
  const v = row.date_updated ?? row.updated_at ?? 0;
  return typeof v === 'number' ? v * 1000 : Date.parse(v) || 0;
};

/**
 * For a page that saves in place and shows what the server returned: the
 * newer of the synced copy and the page's own. Right after a save the
 * page's copy is newer, until the change comes back through the engine a
 * moment later; after that — or after an edit made elsewhere — the synced
 * copy is at least as new and wins. Without this, a saved value would
 * flick back for the length of that round trip.
 */
export function fresher<T extends Stamped>(
  synced: T | null | undefined,
  own: T | null
): T | null {
  if (!synced) return own;
  if (!own || own.id !== synced.id) return synced;
  return stamp(own) > stamp(synced) ? own : synced;
}

/** Each member with each association they have a home in — the palette's
 *  member results, one per association as the fetched version had them. */
export function useSyncedMemberPlacements(): { member: MemberReturn; hoaId: string }[] | null {
  const ready = useReady();
  const members = useTable('member');
  const links = useTable('memberHome');
  const homes = useTable('home');
  return useMemo(() => {
    if (!ready) return null;
    const seen = new Set<string>();
    const out: { member: MemberReturn; hoaId: string }[] = [];
    values(links).forEach((l) => {
      const member = members.get(l.member_id);
      const hoaId = homes.get(l.home_id)?.hoa_id;
      const key = `${l.member_id}:${hoaId}`;
      if (member && hoaId && !seen.has(key)) {
        seen.add(key);
        out.push({ member, hoaId });
      }
    });
    return out;
  }, [ready, members, links, homes]);
}
